import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearch } from '../../hooks/useSearch';
import { useDebounce } from '../../hooks/useDebounce';

interface SearchModalProps {
  onClose: () => void;
}

export function SearchModal({ onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();

  const { data: results, isLoading } = useSearch(debouncedQuery);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 pb-4 px-4 bg-surface-0/80 backdrop-blur-sm animate-fade-in">
      <div 
        className="fixed inset-0" 
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-2xl bg-surface-1 border border-surface-border rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[70vh]">
        
        {/* Search Input */}
        <div className="flex items-center px-4 py-3 border-b border-surface-border bg-surface-2/50">
          <svg className="w-5 h-5 text-text-muted mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            className="flex-1 bg-transparent border-none focus:outline-none text-text-primary text-lg placeholder:text-text-muted"
            placeholder="Search documents by title or content..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <kbd className="hidden sm:inline-block px-2 py-1 text-xs font-semibold text-text-muted bg-surface-0 border border-surface-border rounded-md">
            ESC
          </kbd>
        </div>

        {/* Results Area */}
        <div className="flex-1 overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="px-4 py-12 text-center text-text-muted text-sm">
              Type at least 2 characters to search...
            </div>
          ) : isLoading ? (
            <div className="px-4 py-8 flex justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
            </div>
          ) : results && results.length > 0 ? (
            <ul className="py-2">
              {results.map((doc) => (
                <li key={doc.id}>
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none transition-colors group flex flex-col gap-1 border-l-2 border-transparent hover:border-brand-500"
                    onClick={() => {
                      navigate(`/app/editor/${doc.id}`);
                      onClose();
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-text-primary group-hover:text-brand-400 transition-colors">
                        {doc.title}
                      </span>
                      <span className="text-xs text-text-muted">
                        {new Date(doc.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                    {doc.snippet && (
                      <p className="text-sm text-text-secondary font-mono bg-surface-0 p-1.5 rounded truncate">
                        {doc.snippet}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-12 text-center text-text-secondary text-sm">
              No results found for "{query}"
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-surface-2/30 border-t border-surface-border text-xs text-text-muted flex items-center justify-center gap-4">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-surface-1 border border-surface-border">↑</kbd>
            <kbd className="px-1.5 py-0.5 rounded bg-surface-1 border border-surface-border">↓</kbd>
            to navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 rounded bg-surface-1 border border-surface-border">Enter</kbd>
            to select
          </span>
        </div>
      </div>
    </div>
  );
}
